import {
  CheckCircle2,
  ChefHat,
  CircleAlert,
  Clock3,
  Loader2,
  Lock,
  MapPin,
  Play,
  Printer,
  Timer,
  UserRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';

import ActionButton from '../../../components/Buttons/ActionButton';

import {
  RestaurantKitchenOrderDTO,
  RestaurantKitchenOrderItemDTO,
  RestaurantKitchenOrderStatus,
  RestaurantKitchenStationPrintState,
} from '../../../types/restaurant/RestaurantKitchenOrderTypes';

const statusStyle: Record<
  RestaurantKitchenOrderStatus,
  { label: string; border: string; badge: string; accent: string }
> = {
  PENDING: {
    label: 'Pendiente',
    border: 'border-warning/50',
    badge: 'bg-warning/10 text-warning',
    accent: 'bg-warning',
  },
  IN_PREPARATION: {
    label: 'Preparando',
    border: 'border-primary/50',
    badge: 'bg-primary/10 text-primary',
    accent: 'bg-primary',
  },
  READY: {
    label: 'Lista',
    border: 'border-success/50',
    badge: 'bg-success/10 text-success',
    accent: 'bg-success',
  },
  CANCELLED: {
    label: 'Cancelada',
    border: 'border-danger/40',
    badge: 'bg-danger/10 text-danger',
    accent: 'bg-danger',
  },
};

const elapsed = (value: string) => {
  const minutes = Math.max(
    0,
    Math.floor((Date.now() - new Date(value).getTime()) / 60000)
  );

  if (minutes < 1) return 'Ahora';
  if (minutes < 60) return `${minutes} min`;

  return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
};

const quantity = (value: number) =>
  Number.isInteger(Number(value)) ? Number(value) : Number(value).toFixed(2);

/**
 * Cuenta regresiva viva (tic cada segundo) hasta {@code target}. En "mm:ss";
 * cuando se pasa muestra el tiempo de más con "+". Cambia de color al acercarse
 * y al vencer.
 *
 * Con {@code panel} se dibuja como bloque grande y resaltado (para el tablero /
 * pantalla completa, que el de cocina mira de lejos); sin él, como texto en línea.
 */
const KitchenCountdown = ({
  target,
  className = '',
  panel = false,
  label,
}: {
  target: string;
  className?: string;
  panel?: boolean;
  label?: string;
}) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const diffMs = new Date(target).getTime() - now;
  const overdue = diffMs <= 0;
  const totalSeconds = Math.floor(Math.abs(diffMs) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const time = `${overdue ? '+' : ''}${minutes}:${String(seconds).padStart(
    2,
    '0'
  )}`;
  const near = !overdue && diffMs <= 120_000;
  const tone = overdue ? 'text-danger' : near ? 'text-warning' : 'text-primary';
  const aria = overdue ? `Atrasada ${time}` : `Lista en ${time}`;

  if (panel) {
    const panelTone = overdue
      ? 'border-danger/50 bg-danger/10'
      : near
      ? 'border-warning/50 bg-warning/10'
      : 'border-primary/40 bg-primary/10';
    return (
      <div
        className={`flex items-center justify-between gap-3 rounded-lg border px-3 py-1.5 ${panelTone} ${className}`}
        aria-label={aria}
      >
        <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-bodydark2">
          <Timer className="h-3.5 w-3.5" />
          {label ?? (overdue ? 'Atrasada' : 'Listo en')}
        </span>
        <span
          className={`text-xl font-extrabold leading-none tabular-nums sm:text-2xl ${tone}`}
        >
          {time}
        </span>
      </div>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1 font-semibold tabular-nums ${tone} ${className}`}
      aria-label={aria}
    >
      <Timer className="h-3.5 w-3.5" />
      {time}
    </span>
  );
};

/** Bloque grande de "tiempo estimado" estático (comandas pendientes). */
const KitchenEstimatePanel = ({
  minutes,
  className = '',
}: {
  minutes: number;
  className?: string;
}) => (
  <div
    className={`flex items-center justify-between gap-3 rounded-lg border border-stroke bg-gray-2 px-3 py-1.5 dark:border-strokedark dark:bg-meta-4 ${className}`}
  >
    <span className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-bodydark2">
      <Timer className="h-3.5 w-3.5" />
      Tiempo estimado
    </span>
    <span className="leading-none text-black dark:text-white">
      <span className="text-xl font-extrabold tabular-nums sm:text-2xl">
        {minutes}
      </span>
      <span className="ml-1 text-sm font-bold text-bodydark2">min</span>
    </span>
  </div>
);

interface Props {
  order: RestaurantKitchenOrderDTO;
  stationId: number | '';
  actionKey: string | null;
  printStates?: RestaurantKitchenStationPrintState[];
  parallelPreparation?: boolean;
  showTimer?: boolean;
  /** Modo pantalla completa: header mínimo, énfasis en los platos. */
  focus?: boolean;
  /** Botones de comanda (Iniciar / Completar). Ocultos fuera del modo Manual. */
  showOrderActions?: boolean;
  /** Botones por plato (Iniciar / Listo). Ocultos si los platos avanzan por tiempo. */
  showItemActions?: boolean;
  onStart: (id: number) => void;
  onReady: (id: number) => void;
  onStartItem: (orderId: number, itemId: number) => void;
  onReadyItem: (orderId: number, itemId: number) => void;
  onReprint: (id: number) => void;
}

const KitchenOrderCard = ({
  order,
  stationId,
  actionKey,
  printStates = [],
  parallelPreparation = true,
  showTimer = false,
  focus = false,
  showOrderActions = true,
  showItemActions = true,
  onStart,
  onReady,
  onStartItem,
  onReadyItem,
  onReprint,
}: Props) => {
  const style = statusStyle[order.status];
  // Suma del tiempo de cada plato multiplicado por su cantidad
  // (2 hamburguesas de 2 min → 4 min). Coincide con `estimatedReadyAt` del backend.
  const estimateMinutes = order.items.reduce(
    (total, item) =>
      total +
      (item.preparationTimeMinutes ?? 0) * Math.max(1, Math.ceil(item.quantity || 1)),
    0
  );
  // Temporizador vivo mientras se prepara; estimación estática mientras espera.
  const showLiveCountdown =
    showTimer &&
    !!order.estimatedReadyAt &&
    order.status === 'IN_PREPARATION';
  const showEstimate =
    showTimer && order.status === 'PENDING' && estimateMinutes > 0;
  const items = stationId
    ? order.items.filter((item) => item.preparationStationId === stationId)
    : order.items;
  const busy =
    actionKey?.startsWith('order-') || actionKey?.startsWith('item-');

  // Con "Preparación paralela" apagada, un plato solo puede iniciarse cuando
  // todos los que van antes en la comanda ya están listos o cancelados. Se
  // evalúa sobre la comanda completa (no sobre el filtro de estación) para
  // reflejar la regla del backend.
  const sequentiallyBlockedIds = new Set<number>();

  if (!parallelPreparation) {
    let earlierUnfinished = false;

    [...order.items]
      .sort((a, b) => a.orderIndex - b.orderIndex || a.id - b.id)
      .forEach((item) => {
        if (earlierUnfinished && item.status === 'PENDING') {
          sequentiallyBlockedIds.add(item.id);
        }

        if (item.status === 'PENDING' || item.status === 'IN_PREPARATION') {
          earlierUnfinished = true;
        }
      });
  }

  const itemStatusPill = (item: RestaurantKitchenOrderItemDTO) => (
    <span
      className={`rounded-lg px-2 py-1 text-[11px] font-medium ${
        statusStyle[item.status].badge
      }`}
    >
      {statusStyle[item.status].label}
    </span>
  );

  const itemAction = (item: RestaurantKitchenOrderItemDTO) => {
    const itemBusy = actionKey?.startsWith(`item-${item.id}-`);

    // Sin control manual de platos (avance por tiempo): solo se muestra el estado.
    if (!showItemActions) {
      return itemStatusPill(item);
    }

    if (item.status === 'PENDING') {
      const blocked = sequentiallyBlockedIds.has(item.id);

      return (
        <button
          type="button"
          disabled={busy || blocked}
          title={
            blocked
              ? 'Preparación paralela desactivada: termina los platos anteriores de la comanda primero.'
              : undefined
          }
          onClick={() => onStartItem(order.id, item.id)}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-primary/30 px-2 py-1 text-xs font-medium text-primary transition hover:bg-primary/5 disabled:opacity-40"
        >
          {itemBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : blocked ? (
            <Lock className="h-3.5 w-3.5" />
          ) : (
            <Play className="h-3.5 w-3.5" />
          )}
          Iniciar
        </button>
      );
    }

    if (item.status === 'IN_PREPARATION') {
      return (
        <button
          type="button"
          disabled={busy}
          onClick={() => onReadyItem(order.id, item.id)}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-success px-2 py-1 text-xs font-medium text-white transition hover:opacity-90 disabled:opacity-40"
        >
          {itemBusy ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <CheckCircle2 className="h-3.5 w-3.5" />
          )}
          Listo
        </button>
      );
    }

    return itemStatusPill(item);
  };

  return (
    <article
      className={`relative overflow-hidden rounded-xl border bg-white shadow-card transition hover:-translate-y-0.5 hover:shadow-default dark:bg-boxdark ${style.border}`}
    >
      <span className={`absolute inset-y-0 left-0 w-1 ${style.accent}`} />

      {focus ? (
        <header className="border-b border-stroke bg-gray-2/40 px-4 py-3 dark:border-strokedark dark:bg-meta-4/20">
          <div className="flex items-center gap-2">
            <h3 className="min-w-0 flex-1 truncate text-lg font-extrabold leading-tight text-black dark:text-white">
              {order.kitchenNumber}
            </h3>

            <span
              className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${style.badge}`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${style.accent}`} />
              {style.label}
            </span>
          </div>

          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-bodydark2">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {[
                order.diningRoomName,
                order.tableName || order.tableCode || 'Sin mesa',
              ]
                .filter(Boolean)
                .join(' · ')}
            </span>

            <span className="inline-flex items-center gap-1">
              <Clock3 className="h-3.5 w-3.5 text-warning" />
              Enviada hace {elapsed(order.sentAt)}
            </span>
          </div>

          {showLiveCountdown && order.estimatedReadyAt && (
            <KitchenCountdown
              target={order.estimatedReadyAt}
              panel
              className="mt-2.5"
            />
          )}

          {showEstimate && (
            <KitchenEstimatePanel minutes={estimateMinutes} className="mt-2.5" />
          )}

          {order.notes && (
            <p className="mt-2 rounded-lg border border-warning/20 bg-warning/10 px-2.5 py-1.5 text-xs text-black dark:text-white">
              {order.notes}
            </p>
          )}
        </header>
      ) : (
        <header className="border-b border-stroke bg-gray-2/30 p-4 pl-5 dark:border-strokedark dark:bg-meta-4/20">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded-lg bg-primary/10 p-2 text-primary">
                  <ChefHat className="h-4 w-4" />
                </span>

                <h3 className="text-lg font-bold text-black dark:text-white">
                  {order.kitchenNumber}
                </h3>
              </div>

              <p className="mt-1 text-xs text-bodydark2">
                Orden {order.orderNumber}
              </p>
            </div>

            <span
              className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold ${style.badge}`}
            >
              {style.label}
            </span>
          </div>

          <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-stroke bg-white/80 p-3 text-xs text-bodydark2 dark:border-strokedark dark:bg-boxdark/70">
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" />
              {order.tableName || order.tableCode || 'Sin mesa'}
            </span>

            <span className="inline-flex items-center justify-end gap-1.5 font-medium text-black dark:text-white">
              <Clock3 className="h-3.5 w-3.5 text-warning" />
              {elapsed(order.sentAt)}
            </span>

            {order.diningRoomName && <span>{order.diningRoomName}</span>}

            {order.waiterName && (
              <span className="inline-flex items-center justify-end gap-1.5">
                <UserRound className="h-3.5 w-3.5" />
                {order.waiterName}
              </span>
            )}
          </div>

          {showLiveCountdown && order.estimatedReadyAt && (
            <KitchenCountdown
              target={order.estimatedReadyAt}
              panel
              className="mt-3"
            />
          )}

          {showEstimate && (
            <KitchenEstimatePanel minutes={estimateMinutes} className="mt-3" />
          )}

          {order.notes && (
            <p className="mt-3 rounded-lg border border-warning/20 bg-warning/10 px-3 py-2 text-xs text-black dark:text-white">
              {order.notes}
            </p>
          )}
        </header>
      )}

      <div className="divide-y divide-stroke dark:divide-strokedark">
        {items.map((item) => (
          <div key={item.id} className={focus ? 'px-4 py-3' : 'p-4 pl-5'}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p
                  className={`text-black dark:text-white ${
                    focus ? 'text-base font-semibold' : 'font-medium'
                  }`}
                >
                  <strong
                    className={`mr-2 inline-flex justify-center rounded bg-primary/10 text-primary ${
                      focus
                        ? 'min-w-9 px-2 py-0.5 text-sm'
                        : 'min-w-8 px-2 py-0.5'
                    }`}
                  >
                    {quantity(item.quantity)}x
                  </strong>
                  {item.itemName}
                </p>

                {(item.preparationStationName || item.notes) && (
                  <p className="mt-1 truncate text-[11px] text-bodydark2">
                    {item.preparationStationName ||
                      (focus ? '' : 'Sin estación asignada')}
                    {item.notes
                      ? `${item.preparationStationName ? ' · ' : ''}${item.notes}`
                      : ''}
                  </p>
                )}

                {!!item.modifiers?.length && (
                  <ul
                    className={`mt-1 text-bodydark2 ${
                      focus ? 'text-xs' : 'text-xs'
                    }`}
                  >
                    {item.modifiers.map((modifier, index) => (
                      <li
                        key={
                          modifier.id ??
                          modifier.modifierId ??
                          `${modifier.name}-${index}`
                        }
                      >
                        + {modifier.name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {itemAction(item)}
            </div>
          </div>
        ))}
      </div>

      <footer
        className={`flex flex-wrap gap-2 border-t border-stroke dark:border-strokedark ${
          focus ? 'px-4 py-3' : 'p-4 pl-5'
        } ${
          focus &&
          printStates.length === 0 &&
          (Boolean(stationId) ||
            !showOrderActions ||
            (order.status !== 'PENDING' && order.status !== 'IN_PREPARATION'))
            ? 'hidden'
            : ''
        }`}
      >
        {printStates.length > 0 && (
          <div className="w-full space-y-1.5 pb-2" aria-live="polite">
            {printStates.map((printState) => (
              <div
                key={printState.jobId}
                className="flex items-start gap-2 text-xs text-bodydark2"
              >
                {printState.status === 'PRINTING' ? (
                  <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-primary" />
                ) : printState.status === 'PRINTED' ? (
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-success" />
                ) : (
                  <CircleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0 text-danger" />
                )}
                <span>
                  {printState.preparationStationName}:{' '}
                  {printState.status === 'PRINTING'
                    ? 'imprimiendo'
                    : printState.status === 'PRINTED'
                    ? 'impreso'
                    : `error${
                        printState.error ? ` · ${printState.error}` : ''
                      }`}
                </span>
              </div>
            ))}
          </div>
        )}

        {!focus && (
          <ActionButton
            label="Reimprimir comanda"
            icon={
              actionKey === `order-${order.id}-reprint` ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Printer className="h-4 w-4" />
              )
            }
            variant="secondary"
            size="md"
            disabled={Boolean(busy)}
            onClick={() => onReprint(order.id)}
          />
        )}

        {showOrderActions &&
          !stationId &&
          (order.status === 'PENDING' || order.status === 'IN_PREPARATION') && (
            <>
              {order.status === 'PENDING' && (
                <ActionButton
                  label="Iniciar comanda"
                  icon={
                    actionKey === `order-${order.id}-start` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Play className="h-4 w-4" />
                    )
                  }
                  variant="primary"
                  size="md"
                  disabled={Boolean(busy)}
                  onClick={() => onStart(order.id)}
                />
              )}

              {order.status === 'IN_PREPARATION' && (
                <ActionButton
                  label="Completar comanda"
                  icon={
                    actionKey === `order-${order.id}-ready` ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="h-4 w-4" />
                    )
                  }
                  variant="success"
                  size="md"
                  disabled={Boolean(busy)}
                  onClick={() => onReady(order.id)}
                />
              )}
            </>
          )}
      </footer>
    </article>
  );
};

export default KitchenOrderCard;
